import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, CreditCard, Globe, HeadphonesIcon, Shield, TrendingDown } from 'lucide-react';
import { clientTypes } from '../mock';

const Billeterie = () => {
  const services = [
    { icon: CheckCircle2, text: 'Recherche et sélection des meilleures options' },
    { icon: CreditCard, text: 'Émission, modifications, annulations' },
    { icon: TrendingDown, text: 'Optimisation des coûts' },
    { icon: Globe, text: 'Gestion des miles et programmes corporate' },
    { icon: HeadphonesIcon, text: 'Suivi des passagers 24/7' },
    { icon: Shield, text: 'Factures groupées et reporting mensuel' },
    { icon: Clock, text: 'Gestion des imprévus et urgences' },
    { icon: CheckCircle2, text: 'Centralisation pour toutes vos équipes' }
  ];

  const advantages = [
    'Réactivité immédiate',
    'Disponibilité 24/7',
    'Relation directe avec un agent',
    'Service exclusif, non standardisé',
    'Coordination multi-pays',
    'Zéro stress pour vos équipes'
  ];

  return (
    <div style={{ background: 'var(--bg-page)' }}>
      {/* Hero Section */}
      <section style={{
        padding: '180px 0 100px',
        background: 'var(--bg-page)',
        borderBottom: '1px solid var(--border-medium)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <div className="caption" style={{
            color: 'var(--brand-primary)',
            marginBottom: '20px'
          }}>
            Service Corporate 24/7
          </div>
          <h1 className="heading-1" style={{
            color: 'var(--brand-primary)',
            marginBottom: '30px',
            maxWidth: '800px'
          }}>
            Votre billeterie dédiée aux entreprises
          </h1>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            marginBottom: '40px'
          }}>
            Un service professionnel, rapide et centralisé pour tous vos billets d'avion.
          </p>
          <Link to="/contact" className="btn-primary">
            Demander un devis <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Introduction */}
      <section style={{
        padding: '100px 0',
        background: 'var(--bg-page)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 className="heading-3" style={{
                color: 'var(--brand-primary)',
                marginBottom: '30px'
              }}>
                Pourquoi externaliser votre billeterie ?
              </h2>
            </div>
            <div>
              <p className="body-medium" style={{
                color: 'var(--text-secondary)',
                marginBottom: '20px'
              }}>
                Gérer la billeterie en interne détourne vos équipes de leurs missions principales.
              </p>
              <p className="body-medium" style={{
                color: 'var(--text-secondary)',
                marginBottom: '20px'
              }}>
                L'Agence supprime cette charge en assumant toute la complexité des voyages professionnels : recherche, émission, modifications, urgences et assistance continue.
              </p>
              <p className="body-medium" style={{
                color: 'var(--text-secondary)'
              }}>
                Nous collaborons en toute fluidité avec vos équipes RH, finance, logistique ou direction afin de simplifier vos processus, jamais les alourdir.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Included */}
      <section style={{
        padding: '100px 0',
        background: 'var(--secondary-olive)',
        borderTop: '1px solid var(--border-medium)',
        borderBottom: '1px solid var(--border-medium)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <h2 className="heading-2" style={{
            color: 'var(--brand-primary)',
            marginBottom: '60px',
            textAlign: 'center'
          }}>
            Services inclus
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px'
          }}>
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    minWidth: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--brand-primary)',
                    borderRadius: '50%',
                    flexShrink: 0
                  }}>
                    <Icon size={24} color="var(--brand-primary)" />
                  </div>
                  <div>
                    <p className="body-medium" style={{
                      color: 'var(--text-primary)',
                      lineHeight: 1.5
                    }}>
                      {service.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* For Who */}
      <section style={{
        padding: '100px 0',
        background: 'var(--bg-page)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px'
          }}>
            <div>
              <h2 className="heading-2" style={{
                color: 'var(--brand-primary)',
                marginBottom: '30px'
              }}>
                Pour qui ?
              </h2>
              <p className="body-medium" style={{
                color: 'var(--text-secondary)'
              }}>
                Notre service de billeterie corporate s'adresse à toutes les organisations ayant des besoins réguliers ou ponctuels en déplacements professionnels.
              </p>
            </div>
            <div>
              <ul style={{
                listStyle: 'none',
                padding: 0
              }}>
                {clientTypes.map((type, index) => (
                  <li key={index} style={{
                    marginBottom: '15px',
                    paddingLeft: '30px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      color: 'var(--brand-primary)',
                      fontSize: '1.5rem'
                    }}>•</span>
                    <span className="body-medium" style={{
                      color: 'var(--text-secondary)'
                    }}>
                      {type}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section style={{
        padding: '100px 0',
        background: 'var(--bg-card)',
        borderTop: '1px solid var(--border-medium)',
        borderBottom: '1px solid var(--border-medium)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <h2 className="heading-2" style={{
            color: 'var(--brand-primary)',
            marginBottom: '60px',
            textAlign: 'center'
          }}>
            Pourquoi choisir L'Agence ?
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            {advantages.map((advantage, index) => (
              <div key={index} style={{
                background: 'var(--secondary-olive)',
                padding: '30px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <div className="heading-4" style={{
                  color: 'var(--brand-primary)',
                  fontSize: '1.1rem'
                }}>
                  {advantage}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '120px 0',
        background: 'var(--bg-page)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px',
          textAlign: 'center'
        }}>
          <h2 className="heading-2" style={{
            color: 'var(--brand-primary)',
            marginBottom: '30px'
          }}>
            Simplifiez votre billeterie dès aujourd'hui
          </h2>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            marginBottom: '40px',
            maxWidth: '600px',
            margin: '0 auto 40px'
          }}>
            Contactez-nous pour un devis personnalisé et découvrez comment nous pouvons optimiser votre gestion de billets d'avion.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link to="/contact" className="btn-primary">
              Demander un devis <ArrowRight size={20} />
            </Link>
            <a href="tel:+243841223949" className="btn-secondary">
              Appeler maintenant
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Billeterie;